import { zodResolver } from '@hookform/resolvers/zod'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { Plus, Trash2 } from 'lucide-react'
import { useState } from 'react'
import { Controller, useFieldArray, useForm } from 'react-hook-form'

import { FieldError } from '@/components/feedback/FieldError'
import { Loading } from '@/components/feedback/Loading'
import { useToastStack } from '@/components/feedback/toast-stack'
import { DateInput } from '@/components/filters/DateInput'
import { OptionSelect } from '@/components/filters/OptionSelect'
import { Button } from '@/components/ui/button'
import { Checkbox } from '@/components/ui/checkbox'
import { Input } from '@/components/ui/input'
import {
  Sheet,
  SheetBody,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from '@/components/ui/sheet'
import { useInvalidateCash } from '@/features/cash/hooks'
import {
  itemFormSchema,
  itemFormValues,
  itemPlanBody,
  type ItemFormValues,
} from '@/features/cash/item-form'
import { CLASSES } from '@/features/cash/planning-grid'
import { formatDateTime, formatDecimal } from '@/lib/format'
import { fieldErrorProps } from '@/lib/form/field-error'
import { GuardScope, useGuardRegistry, useRegisterGuard } from '@/lib/form/guard-registry'
import { useRecordEditor } from '@/lib/form/use-record-editor'
import { useRecordVersion } from '@/lib/form/use-record-version'
import { messages } from '@/lib/messages'
import { toProblem } from '@/lib/problem'
import { queryKeys } from '@/lib/query-keys'
import {
  cashApi,
  type CashPlanCari,
  type CashPlanItem,
  type CashPlanning,
} from '@/services/http/cash'

// Ödeme planlama yan panelleri (docs/ODEME-PLANLAMA-IZGARA-PLAN.md Faz 2): kalem ayrıntı editörü,
// cariye tutar dağıtma ve satır geçmişi. Izgara `PlanningPage.tsx`'te.

const t = messages.cash.planning

const money = (v: number) => formatDecimal(v, 2)

function useCashMutation<T, R = unknown>(
  fn: (body: T) => Promise<R>,
  success: (result: unknown) => string,
  /** `true` dönerse hata işlenmiş sayılır (ör. kayıt çakışması uyarısı) ve genel toast gösterilmez. */
  handleError?: (error: unknown) => boolean,
) {
  const { toast } = useToastStack()
  const invalidate = useInvalidateCash()
  return useMutation({
    mutationFn: fn,
    onSuccess: (result) => {
      toast({ type: 'success', title: success(result) })
      void invalidate()
    },
    onError: (error) => {
      if (handleError?.(error)) return
      toast({ type: 'error', title: t.failed, description: toProblem(error).detail ?? undefined })
    },
  })
}

function AllocateForm({
  firm,
  cari,
  instruments,
}: {
  firm: string
  cari: CashPlanCari
  instruments: string[]
}) {
  const [amount, setAmount] = useState('')
  const [date, setDate] = useState('')
  const [instrument, setInstrument] = useState('Banka')
  const mutation = useCashMutation(cashApi.allocate, (r) =>
    t.allocated((r as { updated: number }).updated),
  )
  const value = Number(amount.replace(/\./g, '').replace(',', '.'))
  return (
    <form
      className="flex flex-wrap items-end gap-2"
      aria-label={t.allocate}
      onSubmit={(event) => {
        event.preventDefault()
        mutation.mutate(
          { firm, cari_code: cari.code, amount: value, date, instrument },
          { onSuccess: () => setAmount('') },
        )
      }}
    >
      <label className="flex flex-col gap-1 text-xs">
        {t.amount}
        <Input
          inputMode="decimal"
          className="w-40"
          value={amount}
          onChange={(e) => setAmount(e.target.value)}
          aria-label={t.amount}
        />
      </label>
      <label className="flex flex-col gap-1 text-xs">
        {t.date}
        <DateInput value={date} onChange={setDate} aria-label={t.date} />
      </label>
      <label className="flex flex-col gap-1 text-xs">
        {t.instrument}
        <OptionSelect
          value={instrument}
          options={instruments.map((i) => ({ value: i, label: i }))}
          onValueChange={setInstrument}
          aria-label={t.instrument}
          className="w-48"
        />
      </label>
      <Button
        type="submit"
        disabled={!(value > 0) || !date || mutation.isPending}
        loading={mutation.isPending}
      >
        {t.submit}
      </Button>
    </form>
  )
}

function ItemEditor({
  firm,
  item,
  instruments,
  onDone,
  onReloaded,
}: {
  firm: string
  item: CashPlanItem
  instruments: string[]
  onDone: () => void
  /** "Yeniden yükle" sonrası kalemin güncel hâli (`null`: kalem artık yok); editör ondan yeniden kurulur. */
  onReloaded: (fresh: CashPlanItem | null) => void
}) {
  const form = useForm<ItemFormValues>({
    resolver: zodResolver(itemFormSchema),
    defaultValues: itemFormValues(item),
  })
  const parts = useFieldArray({ control: form.control, name: 'parts' })
  const errors = form.formState.errors
  const queryClient = useQueryClient()
  const { toast } = useToastStack()
  const invalidate = useInvalidateCash()
  // Editör açılışta kalemden kopyalanan bir taslaktır: sürüm açılıştaki kalemin sürümüne sabitlenir
  // (`dirty: true`), arka plandaki tazeleme ya da gerçek zamanlı invalidation onu ilerletmez.
  const version = useRecordVersion({
    recordId: item.key,
    loaded: item.version,
    dirty: true,
    reload: async () => {
      const fresh = await queryClient.fetchQuery({
        queryKey: queryKeys.cash.planning(firm),
        queryFn: () => cashApi.planning(firm),
        staleTime: 0,
      })
      onReloaded(fresh.caris.flatMap((c) => c.items).find((i) => i.key === item.key) ?? null)
    },
  })
  const editor = useRecordEditor({
    form,
    submit: (values) => cashApi.saveItem(itemPlanBody(firm, item, values, version.current())),
    onSaved: (saved) => {
      version.advance(saved.version)
      // Tazeleme gelene kadar hızlı yeniden açılış yeni sürümle başlasın (sahte 409 olmasın).
      queryClient.setQueryData<CashPlanning>(
        queryKeys.cash.planning(firm),
        (old) =>
          old && {
            ...old,
            caris: old.caris.map((c) => ({
              ...c,
              items: c.items.map((i) =>
                i.key === item.key ? { ...i, version: saved.version } : i,
              ),
            })),
          },
      )
      toast({ type: 'success', title: t.saved })
      void invalidate()
      onDone()
    },
    version,
    failedTitle: t.failed,
  })
  useRegisterGuard(editor.requestClose)
  const options = instruments.map((i) => ({ value: i, label: i }))
  const id = `cash-item-${item.key}`

  return (
    <form
      className="flex flex-col gap-3"
      aria-label={t.edit}
      noValidate
      onSubmit={(event) => void editor.save(event)}
    >
      {version.alert}
      {version.dialog}
      {editor.dialog}
      <div className="flex flex-col gap-2">
        <span className="text-xs font-medium">{t.parts}</span>
        {parts.fields.map((field, index) => {
          const amountError = errors.parts?.[index]?.amount?.message
          return (
            <div key={field.id} className="flex flex-col gap-1">
              <div className="flex flex-wrap items-center gap-2">
                <Controller
                  control={form.control}
                  name={`parts.${index}.date`}
                  render={({ field: date }) => (
                    <DateInput
                      value={date.value}
                      onChange={date.onChange}
                      aria-label={`${t.date} ${index + 1}`}
                    />
                  )}
                />
                <Input
                  inputMode="decimal"
                  className="w-36"
                  aria-label={`${t.amount} ${index + 1}`}
                  {...fieldErrorProps(`${id}-amount-${index}`, amountError)}
                  {...form.register(`parts.${index}.amount`)}
                />
                <Controller
                  control={form.control}
                  name={`parts.${index}.instrument`}
                  render={({ field: instrument }) => (
                    <OptionSelect
                      value={instrument.value}
                      options={options}
                      onValueChange={instrument.onChange}
                      aria-label={`${t.instrument} ${index + 1}`}
                      className="w-44"
                    />
                  )}
                />
                <Button
                  type="button"
                  variant="ghost"
                  size="icon-sm"
                  aria-label={t.removePart}
                  onClick={() => parts.remove(index)}
                >
                  <Trash2 />
                </Button>
              </div>
              <FieldError id={`${id}-amount-${index}`} message={amountError} />
            </div>
          )
        })}
        <Button
          type="button"
          variant="secondary"
          size="sm"
          className="w-fit"
          onClick={() => parts.append({ date: '', amount: '', instrument: 'Banka' })}
        >
          <Plus />
          {t.addPart}
        </Button>
      </div>
      <div className="flex flex-wrap items-end gap-3">
        <label className="flex flex-col gap-1 text-xs">
          {t.remainderDate}
          <Controller
            control={form.control}
            name="remainder_date"
            render={({ field }) => (
              <DateInput
                value={field.value}
                onChange={field.onChange}
                aria-label={t.remainderDate}
              />
            )}
          />
        </label>
        <label className="flex flex-col gap-1 text-xs">
          {t.instrument}
          <Controller
            control={form.control}
            name="remainder_instrument"
            render={({ field }) => (
              <OptionSelect
                value={field.value}
                options={options}
                onValueChange={field.onChange}
                aria-label={`${t.remainderDate} ${t.instrument}`}
                className="w-44"
              />
            )}
          />
        </label>
        <label className="flex flex-col gap-1 text-xs">
          {t.klass}
          <Controller
            control={form.control}
            name="klass"
            render={({ field }) => (
              <OptionSelect
                value={field.value}
                options={CLASSES.map((c) => ({ value: c, label: c }))}
                onValueChange={field.onChange}
                aria-label={t.klass}
                className="w-32"
              />
            )}
          />
        </label>
        <label className="flex items-center gap-2 text-sm">
          <Controller
            control={form.control}
            name="erp_outside"
            render={({ field }) => (
              <Checkbox checked={field.value} onCheckedChange={(v) => field.onChange(!!v)} />
            )}
          />
          {t.erpOutside}
        </label>
      </div>
      <label className="flex flex-col gap-1 text-xs">
        {t.note}
        <Input
          aria-label={t.note}
          {...fieldErrorProps(`${id}-note`, errors.note?.message)}
          {...form.register('note')}
        />
        <FieldError id={`${id}-note`} message={errors.note?.message} />
      </label>
      <div className="flex gap-2">
        <Button type="submit" loading={editor.saving}>
          {t.save}
        </Button>
        <Button type="button" variant="secondary" onClick={() => editor.requestClose(onDone)}>
          {t.cancel}
        </Button>
      </div>
    </form>
  )
}

export function HistoryPanel({
  firm,
  item,
  onClose,
}: {
  firm: string
  item: CashPlanItem | null
  onClose: () => void
}) {
  const history = useQuery({
    queryKey: queryKeys.cash.history(firm, 'kalem', item?.key ?? ''),
    queryFn: () => cashApi.history(firm, 'kalem', item!.key),
    enabled: !!item,
  })
  return (
    <Sheet open={item !== null} onOpenChange={(open) => !open && onClose()}>
      <SheetContent side="right">
        <SheetHeader>
          <SheetTitle>{t.historyTitle}</SheetTitle>
          <SheetDescription>{item ? `${item.doc_no ?? item.key}` : ''}</SheetDescription>
        </SheetHeader>
        <SheetBody>
          {history.isLoading ? (
            <Loading rows={3} />
          ) : !history.data?.length ? (
            <p className="text-text-secondary text-sm">{t.historyEmpty}</p>
          ) : (
            <ol className="flex flex-col gap-3" aria-label={t.historyTitle}>
              {history.data.map((h) => (
                <li key={h.at} className="border-b pb-2 text-sm last:border-0">
                  <span className="block font-medium">{t.actions[h.action] ?? h.action}</span>
                  <span className="text-text-secondary block text-xs">
                    {t.lastChange(h.user_name ?? messages.common.unknown, formatDateTime(h.at))}
                  </span>
                </li>
              ))}
            </ol>
          )}
        </SheetBody>
      </SheetContent>
    </Sheet>
  )
}

/**
 * Kalemin ayrıntı editörü yan panelde. Kapatma (dışarı tıklama, Esc) kirli formda önce onay sorar;
 * "Yeniden yükle"den gelen güncel kalemle form yeniden kurulur. Ekran her açılışta `key` verir.
 */
export function ItemSheet({
  firm,
  item,
  instruments,
  onClose,
}: {
  firm: string
  item: CashPlanItem | null
  instruments: string[]
  onClose: () => void
}) {
  const guards = useGuardRegistry()
  const [reloaded, setReloaded] = useState<{ item: CashPlanItem; epoch: number } | null>(null)
  const current = reloaded?.item ?? item
  return (
    <Sheet open={item !== null} onOpenChange={(open) => !open && guards.guarded(onClose)}>
      <SheetContent side="right">
        <SheetHeader>
          <SheetTitle>{t.editTitle}</SheetTitle>
          <SheetDescription>{current ? (current.doc_no ?? current.key) : ''}</SheetDescription>
        </SheetHeader>
        <SheetBody>
          <GuardScope value={guards}>
            {current && (
              <ItemEditor
                key={reloaded?.epoch ?? 0}
                firm={firm}
                item={current}
                instruments={instruments}
                onDone={onClose}
                onReloaded={(fresh) =>
                  fresh
                    ? setReloaded((prev) => ({ item: fresh, epoch: (prev?.epoch ?? 0) + 1 }))
                    : onClose()
                }
              />
            )}
          </GuardScope>
        </SheetBody>
      </SheetContent>
    </Sheet>
  )
}

/** Cariye tutar dağıtma yan panelde; başında carinin açık/planlı/plansız toplamları. */
export function AllocateSheet({
  firm,
  cari,
  instruments,
  onClose,
}: {
  firm: string
  cari: CashPlanCari | null
  instruments: string[]
  onClose: () => void
}) {
  return (
    <Sheet open={cari !== null} onOpenChange={(open) => !open && onClose()}>
      <SheetContent side="right">
        <SheetHeader>
          <SheetTitle>{t.allocateTitle}</SheetTitle>
          <SheetDescription>
            {cari ? `${cari.name ?? cari.code} · ${cari.code}` : ''}
          </SheetDescription>
        </SheetHeader>
        <SheetBody>
          {cari && (
            <div className="flex flex-col gap-4">
              <dl className="grid grid-cols-3 gap-2 text-xs">
                {[
                  [t.open, cari.open_total],
                  [t.planned, cari.planned_total],
                  [t.unplanned, cari.unplanned_total],
                ].map(([label, value]) => (
                  <div key={label as string} className="flex flex-col gap-0.5">
                    <dt className="text-text-secondary">{label}</dt>
                    <dd className="font-medium tabular-nums">{money(value as number)}</dd>
                  </div>
                ))}
              </dl>
              <AllocateForm firm={firm} cari={cari} instruments={instruments} />
            </div>
          )}
        </SheetBody>
      </SheetContent>
    </Sheet>
  )
}
